// RC8 · Long-form briefing narrative renderer (PO 2026-09-21).
//
// Renders an authoritative long-form Markdown briefing (e.g. an
// authentic Weekly Cruise Intelligence Brief) inside the dedicated
// reader — beneath the deterministic engine summary sections.
//
// Safety:
//   • `skipHtml` + no rehype-raw → raw HTML / <script> are never
//     parsed into live DOM (dropped).
//   • GFM tables/strikethrough via remark-gfm.
//   • Every link is routed through `safeHref` (drops javascript:,
//     data:, vbscript:, file:) and gets hardened rel/target.

import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { safeHref, externalLinkProps } from "./longFormLinks";

const COMPONENTS = {
  h1: ({ node, ...p }) => <h2 className="mt-8 font-display font-bold text-[#0F172A] text-[24px] leading-tight" {...p} />,
  h2: ({ node, ...p }) => <h3 className="mt-7 font-display font-semibold text-[#0F172A] text-[20px] leading-snug" {...p} />,
  h3: ({ node, ...p }) => <h4 className="mt-6 font-display font-semibold text-[#0F172A] text-[17px]" {...p} />,
  h4: ({ node, ...p }) => <h5 className="mt-5 font-semibold text-[#0F172A] text-[15px]" {...p} />,
  p: ({ node, ...p }) => <p className="mt-3 text-[15px] leading-[1.7] text-[#334155]" {...p} />,
  ul: ({ node, ...p }) => <ul className="mt-3 ml-5 list-disc space-y-1.5 text-[15px] leading-[1.7] text-[#334155]" {...p} />,
  ol: ({ node, ...p }) => <ol className="mt-3 ml-5 list-decimal space-y-1.5 text-[15px] leading-[1.7] text-[#334155]" {...p} />,
  li: ({ node, ...p }) => <li className="pl-1" {...p} />,
  blockquote: ({ node, ...p }) => <blockquote className="mt-4 border-l-2 border-[#C28E42]/50 pl-4 italic text-[#475569]" {...p} />,
  hr: () => <hr className="my-8 border-slate-200" />,
  strong: ({ node, ...p }) => <strong className="font-semibold text-[#0F172A]" {...p} />,
  code: ({ node, ...p }) => <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[13px] text-[#0F172A]" {...p} />,
  table: ({ node, ...p }) => (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full text-left text-[13.5px] border-collapse" {...p} />
    </div>
  ),
  th: ({ node, ...p }) => <th className="border-b border-slate-300 px-3 py-2 font-semibold text-[#0F172A]" {...p} />,
  td: ({ node, ...p }) => <td className="border-b border-slate-100 px-3 py-2 align-top text-[#334155]" {...p} />,
  a: ({ node, href, children, ...p }) => {
    const h = safeHref(href);
    if (!h) return <span {...p}>{children}</span>;
    return (
      <a href={h} className="text-[#8A5E1E] underline underline-offset-2 hover:text-[#0F172A]" {...externalLinkProps(h)} {...p}>
        {children}
      </a>
    );
  },
};

export function BriefingLongForm({ markdown }) {
  if (!markdown || typeof markdown !== "string" || !markdown.trim()) return null;
  return (
    <section className="mt-14 border-t border-slate-200 pt-8" data-testid="briefing-long-form">
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.24em] text-[#C28E42]">
        Full Briefing
      </div>
      <div className="mt-5" data-testid="briefing-long-form-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml components={COMPONENTS}>
          {markdown}
        </ReactMarkdown>
      </div>
    </section>
  );
}

export default BriefingLongForm;
